import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { articlesAPI, libraryAPI, notesAPI, readingAPI } from '../api';
import { BACKEND_URL } from 'apiConfig';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { toast } from 'sonner';
import {
  ArrowLeft,
  BookOpen,
  BookmarkPlus,
  BookmarkCheck,
  ExternalLink,
  ThumbsUp,
  ThumbsDown,
  CheckCircle2,
  Circle,
  StickyNote,
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  Loader2,
  Headphones,
  Play,
  Lock,
} from 'lucide-react';
import AudioPlayer from '../components/AudioPlayer';
import { LitScholarPanel } from '../components/LitScholarPanel';

const ArticleDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [article, setArticle] = useState(null);
  const [notes, setNotes] = useState([]);
  const [showNotes, setShowNotes] = useState(false);
  const [newNoteBody, setNewNoteBody] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteBody, setEditingNoteBody] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  // Audio state
  const [audioStatus, setAudioStatus] = useState(null);
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  
  const canAudio = user?.capabilities?.premium_audio === true;
  const canCopilot = user?.capabilities?.copilot_evidence_brief === true;
  const litScholarEnabled = useFeatureFlag('enable_litscholar_v1');
  
  // LitScholar panel state
  const [litScholarOpen, setLitScholarOpen] = useState(false);

  useEffect(() => {
    loadArticle();
  }, [id]);

  const loadArticle = async () => {
    try {
      setLoading(true);
      const data = await articlesAPI.getDetail(id);
      setArticle(data);

      // Record that user opened this article
      await readingAPI.recordOpened(id);

      // Load notes
      const notesData = await notesAPI.getNotesForArticle(id);
      setNotes(notesData);
      
      // Load audio status if premium
      if (user?.capabilities?.premium_audio) {
        try {
          const audio = await articlesAPI.getAudioSummary(data.pmid || id);
          setAudioStatus(audio);
        } catch { /* audio not available */ }
      }
    } catch (error) {
      console.error('Error loading article:', error);
      toast.error('Failed to load article');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAudio = async () => {
    setGeneratingAudio(true);
    try {
      await articlesAPI.generateAudio(article.pmid || id);
      const audio = await articlesAPI.getAudioSummary(article.pmid || id);
      setAudioStatus(audio);
      if (audio.status === 'ready') toast.success('Audio takeaway ready!');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (detail?.error_code === 'premium_required') {
        toast.error('Pro subscription required for audio.');
      } else if (detail?.error_code === 'audio_quota_exceeded') {
        toast.error(detail.message || 'Audio quota exceeded.');
      } else {
        toast.error('Audio generation failed.');
      }
    } finally {
      setGeneratingAudio(false);
    }
  };

  const handleSaveToLibrary = async () => {
    try {
      await libraryAPI.saveArticle(article.pmid || article.article_id);
      setArticle(prev => ({
        ...prev,
        user_state: { ...prev.user_state, saved_to_library: true }
      }));
      toast.success('Saved to library!');
    } catch (error) {
      if (error.response?.status === 409) {
        toast.info('Already in your library');
      } else {
        toast.error('Failed to save');
      }
    }
  };

  const handleRemoveFromLibrary = async () => {
    try {
      await libraryAPI.removeArticle(article.pmid || article.article_id);
      setArticle(prev => ({
        ...prev,
        user_state: { ...prev.user_state, saved_to_library: false }
      }));
      toast.success('Removed from library');
    } catch (error) {
      toast.error('Failed to remove');
    }
  };

  const handleFeedback = async (feedback) => {
    try {
      await libraryAPI.submitFeedback(article.pmid || article.article_id, feedback);
      setArticle(prev => ({
        ...prev,
        user_state: { ...prev.user_state, relevance_feedback: feedback }
      }));
      toast.success('Feedback recorded!');
    } catch (error) {
      toast.error('Failed to submit feedback');
    }
  };

  const handleMarkRead = async () => {
    const newReadStatus = !article.user_state.is_read;
    try {
      await readingAPI.markRead(article.pmid || article.article_id, newReadStatus);
      setArticle(prev => ({
        ...prev,
        user_state: { ...prev.user_state, is_read: newReadStatus }
      }));
      toast.success(newReadStatus ? 'Marked as read' : 'Marked as unread');
    } catch (error) {
      toast.error('Failed to update read status');
    }
  };

  const handleCreateNote = async () => {
    if (!newNoteBody.trim()) return;
    setSavingNote(true);
    try {
      const newNote = await notesAPI.createNote(id, newNoteBody.trim());
      setNotes([newNote, ...notes]);
      setNewNoteBody('');
      toast.success('Note added!');
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setSavingNote(false);
    }
  };

  const handleUpdateNote = async (noteId) => {
    if (!editingNoteBody.trim()) return;
    setSavingNote(true);
    try {
      const updated = await notesAPI.updateNote(noteId, editingNoteBody.trim());
      setNotes(notes.map(n => (n.note_id === noteId ? updated : n)));
      setEditingNoteId(null);
      setEditingNoteBody('');
      toast.success('Note updated!');
    } catch (error) {
      toast.error('Failed to update note');
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await notesAPI.deleteNote(noteId);
      setNotes(notes.filter(n => n.note_id !== noteId));
      toast.success('Note deleted');
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Article not found</p>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </div>
    );
  }

  const { user_state } = article;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div className="flex-1" />
            <Button
              variant={user_state.is_read ? 'default' : 'outline'}
              size="sm"
              onClick={handleMarkRead}
            >
              {user_state.is_read ? (
                <><CheckCircle2 className="h-4 w-4 mr-2" />Read</>
              ) : (
                <><Circle className="h-4 w-4 mr-2" />Mark as Read</>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowNotes(!showNotes)}
            >
              <StickyNote className="h-4 w-4 mr-2" />
              Notes ({notes.length})
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Article Header */}
            <Card>
              <CardContent className="pt-6">
                {/* Design Tags */}
                {article.design_tags && article.design_tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {article.design_tags.map((tag, i) => (
                      <Badge key={i} variant="secondary">{tag}</Badge>
                    ))}
                  </div>
                )}

                <h1 className="responsive-title text-2xl font-bold text-gray-900 mb-4">
                  {article.title}
                </h1>

                <div className="text-sm text-gray-600 mb-4">
                  <p className="font-medium text-blue-600">{article.journal}</p>
                  <p>{article.pub_date} • {article.authors}</p>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-3">
                  {user_state.saved_to_library ? (
                    <Button variant="outline" size="sm" onClick={handleRemoveFromLibrary}>
                      <BookmarkCheck className="h-4 w-4 mr-2 text-green-600" />
                      In Library
                    </Button>
                  ) : (
                    <Button variant="default" size="sm" onClick={handleSaveToLibrary}>
                      <BookmarkPlus className="h-4 w-4 mr-2" />
                      Save to Library
                    </Button>
                  )}
                  {article.url && (
                    <Button variant="outline" size="sm" asChild>
                      <a href={article.url} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4 mr-2" />
                        View on PubMed
                      </a>
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* AI Summary */}
            {article.ai_summary && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">AI Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 leading-relaxed">{article.ai_summary}</p>
                </CardContent>
              </Card>
            )}

            {/* Audio Takeaway (Step 5) */}
            <Card data-testid="audio-takeaway-section">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Headphones className="h-5 w-5 text-blue-600" />
                  Audio Takeaway
                </CardTitle>
              </CardHeader>
              <CardContent>
                {canAudio ? (
                  <div className="space-y-3">
                    {audioStatus?.status === 'ready' && audioStatus.audio_url ? (
                      <>
                        <AudioPlayer
                          src={`${BACKEND_URL}${audioStatus.audio_url}`}
                          title={article.title}
                        />
                        <p className="text-xs text-gray-500">Generated from the article abstract summary. No patient data.</p>
                        <button
                          className="text-xs text-blue-600 hover:underline"
                          onClick={() => setShowTranscript(!showTranscript)}
                          data-testid="toggle-transcript-btn"
                        >
                          {showTranscript ? 'Hide transcript' : 'Show transcript'}
                        </button>
                        {showTranscript && audioStatus.transcript && (
                          <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded mt-2" data-testid="audio-transcript">
                            {audioStatus.transcript}
                          </p>
                        )}
                      </>
                    ) : audioStatus?.status === 'pending' ? (
                      <div className="flex items-center gap-2 text-sm text-amber-700">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Generating audio...
                      </div>
                    ) : audioStatus?.status === 'failed' ? (
                      <div className="space-y-2">
                        <p className="text-sm text-red-600">Audio generation failed.</p>
                        <Button size="sm" variant="outline" onClick={handleGenerateAudio} disabled={generatingAudio}>
                          {generatingAudio ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Play className="h-4 w-4 mr-1" />}
                          Retry
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-sm text-gray-600">Listen to an AI-generated audio summary of this article.</p>
                        <Button size="sm" onClick={handleGenerateAudio} disabled={generatingAudio} data-testid="generate-audio-btn">
                          {generatingAudio ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Headphones className="h-4 w-4 mr-1" />}
                          {generatingAudio ? 'Generating...' : 'Generate Audio'}
                        </Button>
                        <p className="text-xs text-gray-500">Generated from the article abstract summary. No patient data.</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-3" data-testid="audio-locked">
                    <Lock className="h-6 w-6 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">Audio takeaways are a Pro feature.</p>
                    <Button variant="link" size="sm" onClick={() => navigate('/plan')} className="text-blue-600">
                      Upgrade to Pro
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Lit Scholar Panel (Rebranded from Copilot) */}
            <Card data-testid="litscholar-section">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-teal-600" />
                  Lit Scholar
                </CardTitle>
              </CardHeader>
              <CardContent>
                {canCopilot ? (
                  litScholarEnabled ? (
                    <div className="space-y-3">
                      <p className="text-xs text-gray-500">No patient information. Lit Scholar is for literature discussion only.</p>
                      <Button
                        onClick={() => setLitScholarOpen(true)}
                        className="w-full bg-teal-600 hover:bg-teal-700 text-white"
                        data-testid="deep-dive-litscholar-btn"
                      >
                        <BookOpen className="h-4 w-4 mr-2" />
                        Deep Dive with Lit Scholar
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-xs text-gray-500">Lit Scholar is not enabled yet. Contact your administrator.</p>
                    </div>
                  )
                ) : (
                  <div className="text-center py-3" data-testid="litscholar-locked">
                    <Lock className="h-6 w-6 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">Lit Scholar is a Pro feature.</p>
                    <Button variant="link" size="sm" onClick={() => navigate('/plan')} className="text-blue-600">Upgrade to Pro</Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* LitScholar Slide-Over Panel */}
            {article && (
              <LitScholarPanel
                open={litScholarOpen}
                onOpenChange={setLitScholarOpen}
                article={{ pmid: article.pmid, title: article.title }}
              />
            )}

            {/* Key Findings */}
            {article.key_findings && article.key_findings.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Key Findings</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {article.key_findings.map((finding, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-blue-600 mt-1">•</span>
                        <span className="text-gray-700">{finding}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Abstract */}
            {article.abstract && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Abstract</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {article.abstract}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Feedback */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Was this article helpful?</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3">
                  <Button
                    variant={user_state.relevance_feedback === 'useful' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleFeedback('useful')}
                  >
                    <ThumbsUp className="h-4 w-4 mr-2" />
                    Useful
                  </Button>
                  <Button
                    variant={user_state.relevance_feedback === 'not_relevant' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleFeedback('not_relevant')}
                  >
                    <ThumbsDown className="h-4 w-4 mr-2" />
                    Not Relevant
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Notes Panel */}
          {showNotes && (
            <div className="lg:col-span-1">
              <Card className="sticky top-20">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <StickyNote className="h-5 w-5" />
                    My Notes
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Add Note */}
                  <div className="space-y-2">
                    <Textarea
                      placeholder="Add a note..."
                      value={newNoteBody}
                      onChange={(e) => setNewNoteBody(e.target.value)}
                      className="min-h-[80px]"
                    />
                    <Button
                      size="sm"
                      onClick={handleCreateNote}
                      disabled={!newNoteBody.trim() || savingNote}
                    >
                      {savingNote ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Plus className="h-4 w-4 mr-2" />
                      )}
                      Add Note
                    </Button>
                  </div>

                  {/* Notes List */}
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {notes.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">
                        No notes yet
                      </p>
                    ) : (
                      notes.map((note) => (
                        <div
                          key={note.note_id}
                          className="bg-yellow-50 border border-yellow-200 rounded-lg p-3"
                        >
                          {editingNoteId === note.note_id ? (
                            <div className="space-y-2">
                              <Textarea
                                value={editingNoteBody}
                                onChange={(e) => setEditingNoteBody(e.target.value)}
                                className="min-h-[60px]"
                              />
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleUpdateNote(note.note_id)}
                                  disabled={savingNote}
                                >
                                  <Save className="h-3 w-3 mr-1" />
                                  Save
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setEditingNoteId(null);
                                    setEditingNoteBody('');
                                  }}
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                {note.body}
                              </p>
                              <div className="flex items-center justify-between mt-2">
                                <span className="text-xs text-gray-500">
                                  {new Date(note.updated_at).toLocaleDateString()}
                                </span>
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 w-6 p-0"
                                    onClick={() => {
                                      setEditingNoteId(note.note_id);
                                      setEditingNoteBody(note.body);
                                    }}
                                  >
                                    <Edit2 className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 w-6 p-0 text-red-600"
                                    onClick={() => handleDeleteNote(note.note_id)}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArticleDetailPage;
